import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, Search, X } from "lucide-react";
import { Brand } from "@/components/site/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/theme-toggle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { DOC_SECTIONS } from "@/app/docs/sections";
import { cn } from "@/lib/utils";

export function DocsLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [sidebarOpen]);

  const filteredSections = query.trim()
    ? DOC_SECTIONS.map((s) => ({
        ...s,
        links: s.links.filter((l) =>
          l.title.toLowerCase().includes(query.toLowerCase()),
        ),
      })).filter((s) => s.links.length > 0)
    : DOC_SECTIONS;

  return (
    <div className="min-h-screen bg-background">
      {/* Topbar */}
      <header className="sticky top-0 z-40 border-b border-border/40 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={sidebarOpen ? "Close docs sidebar" : "Open docs sidebar"}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <Brand />
          <span className="ml-3 hidden text-sm text-muted-foreground sm:inline">
            / Docs
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="hidden text-sm text-muted-foreground hover:text-foreground sm:inline"
            >
              ← Marketing
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Sidebar */}
        <aside
          className={cn(
            "lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:w-64 lg:shrink-0 lg:border-r lg:border-border/40 lg:pr-6",
            sidebarOpen
              ? "fixed inset-0 z-30 block bg-background pt-16"
              : "hidden lg:block",
          )}
        >
          <ScrollArea className="docs-scroll h-full">
            <div className="space-y-6 py-6 pr-4 lg:pr-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter…"
                  className="h-9 pl-9 text-sm"
                />
              </div>
              {filteredSections.map((section) => (
                <div key={section.title}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.title}
                  </h3>
                  <ul className="space-y-0.5">
                    {section.links.map((link) => (
                      <li key={link.slug || "index"}>
                        <NavLink
                          to={`/docs/${link.slug}`}
                          end={link.slug === ""}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors",
                              isActive
                                ? "bg-foreground/10 text-foreground"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                            )
                          }
                        >
                          <span className="truncate">{link.title}</span>
                          {link.status && (
                            <Badge
                              variant="outline"
                              className="ml-2 h-4 px-1.5 py-0 text-[10px] font-normal"
                            >
                              {link.status}
                            </Badge>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </ScrollArea>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1 lg:pl-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
